import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, ChevronRight, RefreshCw, Phone, Loader2 } from "lucide-react";
import { signInWithCustomToken } from "firebase/auth";
import Navbar from "../Landing/Navbar";
import { sendOtp, verifyOtp } from "../services/api";
import { auth } from "../firebase";
import brandLogo from "../Assets/karanki-new-logo.jpg";

const digitsCount = 4;

const VerifyOtp = () => {
  const [digits, setDigits] = useState(Array(digitsCount).fill(""));
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("OTP sent successfully");
  const [loading, setLoading] = useState(false);
  const [otpState, setOtpState] = useState(null);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const navigate = useNavigate();
  const inputRefs = useRef([]);

  useEffect(() => {
    const savedState = sessionStorage.getItem("karankiOtpState");
    if (!savedState) {
      navigate("/register");
      return;
    }
    setOtpState(JSON.parse(savedState));
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, [navigate]);

  useEffect(() => {
    if (timer <= 0) { setCanResend(true); return; }
    const interval = setInterval(() => setTimer((p) => p - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    if (value && index < digitsCount - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleBackspace = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, digitsCount);
    if (!pasted) return;
    event.preventDefault();
    const next = pasted.split("").slice(0, digitsCount);
    while (next.length < digitsCount) next.push("");
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, digitsCount - 1)]?.focus();
  };

  const handleVerify = async () => {
    setError(""); setSuccessMessage("");
    const otp = digits.join("");
    if (otp.length !== digitsCount) {
      setError(`Enter all ${digitsCount} OTP digits`); return;
    }
    try {
      setLoading(true);
      const response = await verifyOtp(otpState.sessionId, otp, otpState.phone);
      if (!response.firebaseToken) throw new Error("Firebase token missing");
      const credential = await signInWithCustomToken(auth, response.firebaseToken);
      const idToken = await credential.user.getIdToken();
      localStorage.setItem("karankiAuth", JSON.stringify({
        uid: response.uid, role: response.role || "user", phone: otpState.phone,
        profileId: response.profileId || null, idToken,
        sessionStartedAt: new Date().toISOString(),
      }));
      localStorage.setItem("karankiProfilePhone", otpState.phone);
      navigate(response.exists ? "/dashboard" : "/create-profile");
    } catch (err) {
      setError(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setError(""); setSuccessMessage("");
      setCanResend(false); setTimer(60);
      const response = await sendOtp(otpState.phone, otpState.authMode);
      const updatedState = { ...otpState, sessionId: response.sessionId };
      sessionStorage.setItem("karankiOtpState", JSON.stringify(updatedState));
      setOtpState(updatedState);
      setDigits(Array(digitsCount).fill(""));
      setSuccessMessage("OTP resent successfully");
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || "Failed to resend OTP");
    }
  };

  const phoneDisplay = otpState?.phone
    ? `+${otpState.phone.slice(0, 2)} ${otpState.phone.slice(2, 7)} ${otpState.phone.slice(7)}`
    : "";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="relative flex min-h-[calc(100vh-80px)] items-center justify-center px-4 py-8 sm:px-6">
        <div className="absolute inset-0 -z-10 bg-mesh" />

        {loading && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 px-4 py-6">
            <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <Loader2 size={28} className="animate-spin" />
              </div>
              <h2 className="text-lg font-semibold text-ink-900">Please wait</h2>
              <p className="mt-2 text-sm text-ink-500">Verifying your OTP...</p>
            </div>
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-elevated w-full max-w-md p-6 sm:p-9"
        >
          <div className="text-center">
            <img
              src={brandLogo}
              alt="Karanki"
              className="mx-auto h-16 w-auto object-contain shadow-soft ring-1 ring-sand-200"
            />
            <span className="chip mt-5 inline-flex">
              <Lock size={11} /> Secure verification
            </span>
            <h1 className="mt-3 font-display text-3xl font-bold text-ink-800">Verify OTP</h1>
            <p className="mt-2 text-[14px] leading-6 text-ink-500">
              We sent a {digitsCount}-digit code to
            </p>
            {phoneDisplay && (
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-sand-100 px-3 py-1 text-[13px] font-semibold text-ink-700">
                <Phone size={12} /> {phoneDisplay}
              </p>
            )}
          </div>

          {successMessage && (
            <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-center text-[13px] font-semibold text-emerald-700">
              {successMessage}
            </div>
          )}
          {error && (
            <div className="mt-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-center text-[13px] font-semibold text-rose-700">
              {error}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleVerify();
            }}
            className="mt-7"
          >
            <div className="flex items-center justify-center gap-3">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => {
                    handleBackspace(index, e);
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleVerify();
                    }
                  }}
                  onPaste={handlePaste}
                  onFocus={(e) => e.target.scrollIntoView({ behavior: "smooth", block: "center" })}
                  aria-label={`OTP digit ${index + 1}`}
                  className="h-16 w-14 rounded-2xl border-2 border-sand-300 bg-white text-center font-display text-2xl font-bold text-ink-800 outline-none transition-all focus:-translate-y-0.5 focus:border-brand-600 focus:shadow-soft sm:h-16 sm:w-16"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary mt-7 w-full py-4 text-[15px]"
            >
              {loading ? "Verifying..." : (
                <>
                  <ShieldCheck size={16} /> Verify OTP <ChevronRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            {!canResend ? (
              <p className="text-[13px] text-ink-500">
                Resend OTP in <span className="font-bold text-brand-600">{timer}s</span>
              </p>
            ) : (
              <button
                onClick={handleResend}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 transition hover:text-brand-700"
              >
                <RefreshCw size={13} /> Resend OTP
              </button>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default VerifyOtp;
